/**
 * Service for submitting AI response ratings to the conversion server.
 */

import { AIRatingPayload, AIRatingResponse } from "../types/aiRating";

/**
 * Derives the ratings API base URL (origin) from any AI endpoint.
 * e.g. "http://localhost:3000/ai/proxy" -> "http://localhost:3000"
 * e.g. "/ai/v2/chat" -> "" (relative, will use same origin)
 * e.g. "http://localhost:8000/process-query-v4" -> "http://localhost:8000"
 */
export function getRatingBaseUrl(endpoint: string): string {
  try {
    const url = new URL(endpoint, window.location.origin);
    // If it was an absolute URL, return its origin
    if (endpoint.startsWith("http")) {
      return url.origin;
    }
    // Relative URL — use same origin
    return "";
  } catch {
    return "";
  }
}

/**
 * Submit an AI rating to the conversion server.
 * Fails silently with a console warning if the server is unreachable.
 */
export async function submitAIRating(
  baseUrl: string,
  payload: AIRatingPayload,
): Promise<AIRatingResponse | null> {
  const endpoint = `${baseUrl}/ai/ratings`;
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.warn(`AI Rating submission failed: HTTP ${response.status}`);
      return null;
    }

    return (await response.json()) as AIRatingResponse;
  } catch (error) {
    console.warn("AI Rating submission failed:", error);
    return null;
  }
}

export interface AnalyticsRatingPayload {
  stars: number;
  comment?: string;
  endUserId: string;
  analyticsTarget?: string;
}

export async function submitAnalyticsRating(
  baseUrl: string,
  queryLogId: string,
  payload: AnalyticsRatingPayload,
): Promise<AIRatingResponse | null> {
  const endpoint = `${baseUrl}/ai/analytics-ratings/${encodeURIComponent(queryLogId)}`;
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      console.warn(`Analytics rating submission failed: HTTP ${response.status}`);
      return null;
    }

    return (await response.json()) as AIRatingResponse;
  } catch (error) {
    console.warn("Analytics rating submission failed:", error);
    return null;
  }
}
